import { ChangeDetectorRef, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BehaviorSubject,
  interval,
  map,
  Observable,
  of,
  repeat,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import {
  OrcamentoByIdResponse,
  ResquestBudget,
  STATUS_ID_MAP_NUMBER,
} from '../../../../models/orcamento.models';
import { NewApiOrcamentoService } from '../../../api/new-api-orcamento/new-api-orcamento.service';
import {
  FormOrchestratorTableData,
  FormTableColumns,
} from '@brunsker/libs/core/ui/table/src';
import {
  MapperService,
  OrcamentoDataDisplayColumns,
  SelectClientService,
} from '@brunsker/libs/portal-do-vendedor/utils/portal-vendedor-api';
import { OrcamentoDataBottom } from '@brunsker/libs/portal-do-vendedor/ui/cart-data-bottom-table';
import { BrazilianCurrencyPipe } from '@brunsker/libs/core/utils/brazilian-currency/src/lib/pipes/brazilian-currency/brazilian-currency.pipe';
import { ObservationDataModel } from '@brunsker/libs/portal-do-vendedor/ui/observations-text-area';

@Injectable({
  providedIn: 'root',
})
export class OrcamentoDomainService {
  hasClient = this.selectClientService.hasClient$$;
  dataOrcamento$!: Observable<OrcamentoByIdResponse>;
  dataDisplay!: Observable<
    FormOrchestratorTableData<OrcamentoDataDisplayColumns>[]
  >;
  dataResquestBudget: ResquestBudget = {
    produtos: [],
    tipoDescontoRodape: 0,
    descontoRodape: 0,
    frete: 0,
  };
  STATUS_ID_MAP_NUMBER = STATUS_ID_MAP_NUMBER;
  columnsOptions: FormTableColumns[] =
    this.mapperService.createOrcamentoColumnsOptions();
  currentSeqPedido!: string;
  currentOrcamentoId!: string | null;
  currentStatus!: string;
  observationData!: Observable<ObservationDataModel>;
  orcamentoDataBottom$$ = new BehaviorSubject<OrcamentoDataBottom>({
    totalItens: 0,
    subTotal: 0,
    desconto: 0,
    frete: 0,
    total: 0,
  });
  observation$$ = new BehaviorSubject<{
    nota?: string;
    orcamento?: string;
    orcamento2?: string;
    orcamento3?: string;
    entrega?: string;
    entrega2?: string;
    entrega3?: string;
  }>({
    nota: '',
    orcamento: '',
    entrega: '',
  });
  dialogStatusData$$ = new BehaviorSubject<{
    status: number;
    orcamento: number;
  }>({
    status: 0,
    orcamento: 0,
  });
  orcamentoDate!: string;
  desconto!: number;
  disableEdit: boolean = false;
  disableGerarPedido: boolean = false;
  orcamento!: OrcamentoByIdResponse;
  statusProgress: { status: number; name: string; reached: boolean }[] = [];
  changeDetector!: ChangeDetectorRef;
  transformA_PEDIDO!: Observable<string>;
  activatedRoute!: ActivatedRoute;
  timeLog!: number;

  constructor(
    protected readonly newApiOrcamento: NewApiOrcamentoService,
    protected readonly mapperService: MapperService,
    protected readonly brazilianCurrencyPipe: BrazilianCurrencyPipe,
    private readonly selectClientService: SelectClientService,
  ) {}

  startDomain(
    activatedRoute: ActivatedRoute,
    changeDetectorRef: ChangeDetectorRef,
  ): void {
    this.currentSeqPedido = activatedRoute.snapshot.queryParams['id'] as string;
    if (!this.currentSeqPedido) {
      window.history.back();
      return;
    }
    this.statusProgress = [];
    this.disableEdit = false;
    this.changeDetector = changeDetectorRef;
    this.activatedRoute = activatedRoute;
    this.orcamentoDataBottom$$.next(null as any);
    this.updateDataOrcamento();
    this.updateObservationData();
    this.updateDataDisplay();
  }

  updateDataOrcamento(totalDiscountRodape?: any, valuePrefix?: any) {
    const currentClient = this.selectClientService.getCliente();
    const user = JSON.parse(sessionStorage.getItem('user') ?? '');

    this.dataOrcamento$ = timer(0, 10000) // Intervalo de 10000 milissegundos (10 segundos)
      .pipe(
        switchMap(
          () =>
            (this.dataOrcamento$ = this.newApiOrcamento
              .getOrcamentoByIdSockt({
                SeqPedido: this.currentSeqPedido,
                CodigoUsuario: user.idVendedor,
                CodigoFilial: 1,
                Matricula: user.idFuncionario,
              })
              .pipe(
                tap((orcamento) => {
                  this.statusProgress = [];
                  this.timeLog = new Date(
                    orcamento.statusLog[orcamento?.statusLog!.length - 1]
                      .DateLog,
                  ).getTime();

                  if (
                    (orcamento.transformA_PEDIDO.toLocaleUpperCase() === 'S' ||
                      orcamento.transformA_PEDIDO.toLocaleUpperCase() ===
                        'N') &&
                    orcamento.status !== 1 &&
                    orcamento.status !== 14
                  ) {
                    this.disableEdit = true;
                  } else {
                    this.disableEdit = false;
                  }

                  this.transformA_PEDIDO = of(orcamento.transformA_PEDIDO);

                  this.mapperService.hasOrcamento$$.next({
                    show: true,
                    orcamentoID: orcamento.n_ORC_GERAL,
                    seqPedido: orcamento.seQ_PEDIDO,
                  });
                  this.dataResquestBudget.produtos = [];
                  orcamento.items_pedido.forEach((item: any) => {
                    let produto = {
                      productId: item.codigo,
                      embalagem: item.embalagem,
                      quantity: item.qtproduto,
                      valorTabela: item.vlprodutotabela,
                      descontoUnitario: item.desconto,
                      tipoDesconto: valuePrefix !== '%' ? 2 : 1,
                    };
                    this.dataResquestBudget.produtos.push(produto);
                  });
                  this.dataResquestBudget.descontoRodape =
                    totalDiscountRodape ??
                    orcamento.percentualDescontoRodape ??
                    0;
                  this.dataResquestBudget.frete = orcamento.vlfrete;
                  this.dataResquestBudget.tipoDescontoRodape =
                    valuePrefix !== '%' ? 2 : 1;

                  this.changeDetector.detectChanges();
                }),
              )),
        ),
      );
  }

  updateObservationData() {
    this.observationData = this.dataOrcamento$?.pipe(
      map((orcamento) => {
        this.orcamento = orcamento;
        this.disableGerarPedido =
          orcamento.n_ORC_GERAL === 0 || orcamento.status !== 1;
        this.transformA_PEDIDO = of(orcamento.transformA_PEDIDO);
        this.changeDetector.detectChanges();

        for (let log of orcamento.statusLog) {
          if (
            log.status !==
            this.statusProgress[this.statusProgress.length - 1]?.status
          ) {
            this.statusProgress = [
              ...this.statusProgress,
              {
                status: log.status,
                name: log.message,
                reached: true,
              },
            ];
          }
        }
        if (
          orcamento.status !== 11 &&
          orcamento.status !== 12 &&
          orcamento.status !== 14
        ) {
          console.log(orcamento.status);

          this.statusProgress.push({
            status: -1,
            name: '',
            reached: false,
          });
          this.statusProgress.push({
            status: -1,
            name: '',
            reached: false,
          });
        }

        this.orcamentoDataBottom$$.next({
          totalItens: orcamento.items_pedido.length,
          subTotal: orcamento.suB_TOTAL,
          frete: orcamento.vlfrete,
          desconto: orcamento.suB_TOTAL - orcamento.vltotal,
          total: orcamento.vltotal + orcamento.vlfrete,
        });
        this.observation$$.next({
          nota: orcamento.nfE_OBS!,
          orcamento: orcamento.obS_PED,
          orcamento2: orcamento.obS_PED2,
          orcamento3: orcamento.obS_PED3,
          entrega: orcamento.obsentrega,
          entrega2: orcamento.obsentregA2,
          entrega3: orcamento.obsentregA3,
        });

        this.dialogStatusData$$.next({
          status: orcamento.status,
          orcamento: orcamento.seQ_PEDIDO,
        });
        const userStringify = sessionStorage.getItem('user');
        const user = userStringify && JSON.parse(userStringify);

        const isValueOrPercent = orcamento.percentualDescontoRodape
          ? 'percentualDescontoRodape'
          : 'valorDescontoRodape';
        const hasDiscountRodaPe =
          !user.permissaoDescontoRodape && orcamento[isValueOrPercent] > 0;

        this.currentOrcamentoId = orcamento.n_ORC_GERAL.toString();
        if (
          (orcamento.transformA_PEDIDO.toLocaleUpperCase() === 'S' ||
            orcamento.transformA_PEDIDO.toLocaleUpperCase() === 'A') &&
          orcamento.status !== 1 &&
          orcamento.status !== 14
        ) {
          this.disableEdit = true;
        }
        return {
          cliente: `${
            orcamento.iD_CLIENTE
          } - ${orcamento.nomE_CLIENTE.toUpperCase()}`,
          endereco: `
              ${orcamento.enderecoentrega || orcamento.bairrO_ENTREGA || orcamento.endereco || ''}
              - Nº ${orcamento.numeroentrega ?? ''}
              - ${orcamento.bairroentrega || orcamento.bairro || orcamento.bairrO_ENTREGA || ''}
              - ${orcamento.cidadeentrega || orcamento.cidadE_ENTREGA || orcamento.cidade || ''}
              - ${orcamento.ufentrega || orcamento.uf || ''}
              - ${orcamento.cepentrega || orcamento.cep || ''}
            `,
          telefone: orcamento.telefonE1 ?? orcamento.celular,
          formaDePagamento: orcamento.nomE_COBRANCA,
          parcelas: orcamento.planO_PAGAMENTO ?? 0,
          tipoVenda: orcamento.tipovenda,
          limitecredito: `${orcamento.limitecredito}`,
        };
      }),
    ) as any;
  }

  updateDataDisplay() {
    const MAP_DATA_COLUMNS = this.dataOrcamento$.pipe(
      map((data) => {
        this.transformA_PEDIDO = of(data.transformA_PEDIDO);
        this.currentStatus = STATUS_ID_MAP_NUMBER[data.status]
          ? STATUS_ID_MAP_NUMBER[data.status].replace(
              /[\sáàâãéèêíïóôõöúçñÁÀÂÃÉÈÊÍÏÓÔÕÖÚÇÑ]+/g,
              '',
            )
          : '';
        this.orcamentoDate = data.datA_INCLUSAO;

        let desconto = data.items_pedido.reduce(
          (acc, item) => acc + item.vlproduto * item.qtproduto,
          0,
        );

        this.desconto = desconto;

        return data.items_pedido.map((item, index) => {
          const numeroSequencial = (index += 1);
          if (
            this.activatedRoute.snapshot.queryParams['id'] &&
            this.activatedRoute.routeConfig?.path === 'orcamento/:id'
          ) {
            item.desconto *= -1;
          }
          return {
            id: numeroSequencial,
            produtos: `${item.codigo} - ${item.nomE_PRODUTO}`,
            embalagem: [item.nomeEmbalagem, item.un].filter(Boolean).join(' - '),
            filial: item.iD_FILIAL,
            desconto: `${item.desconto}%`,
            valorUnitario: item.vlprodutotabela,
            valorUnitarioComDesconto: item.vlproduto,
            quantidade: item.qtproduto,
            subTotal: this.brazilianCurrencyPipe.transform(item.totaL_ITEM),
            observacao: {
              observacao: item.obS_PROD,
              ambiente: item.ambiente,
            },
            retiradaImediata: item.retiradaImediata,
            formGroupValue: [
              {
                formControlName: 'num',
                value: (index += 1),
              },
              {
                formControlName: 'produto',
                value: `${item.codigo} - ${item.nomE_PRODUTO}`,
              },
              {
                formControlName: 'embalagem',
                value: [item.nomeEmbalagem, item.un].filter(Boolean).join(' - '),
              },
              {
                formControlName: 'filial',
                value: item.iD_FILIAL,
              },
              {
                formControlName: 'desconto',
                value: item.desconto,
              },
              {
                formControlName: 'valorUnitario',
                value: item.vlproduto,
              },
              {
                formControlName: 'valorUnitarioComDesconto',
                value: item.vlproduto,
              },
              {
                formControlName: 'retiradaImediata',
                value: item.retiradaImediata,
              },
              {
                formControlName: 'quantidade',
                value: item.qtproduto,
              },
              {
                formControlName: 'subTotal',
                value: item.totaL_ITEM,
              },
              {
                formControlName: 'observation',
                value: {
                  observacao: item.obS_PROD,
                  ambiente: item.ambiente,
                },
                isEditable: false,
              },
            ],
          } as FormOrchestratorTableData<OrcamentoDataDisplayColumns>;
        });
      }),
    );

    this.dataDisplay = MAP_DATA_COLUMNS.pipe(
      tap((data) => {
        this.changeDetector.detectChanges();
      }),
    );
  }

  getValueBudget(budget: ResquestBudget) {
    return this.newApiOrcamento
      .calculoOrcamento(budget)
      .pipe(map((resultado) => resultado));
  }

  transformaOrca() {
    return this.newApiOrcamento.transformInOrder(this.orcamento.seQ_PEDIDO);
  }

  onDestroi(): void {
    this.newApiOrcamento.desconectSockt();
  }
}
